import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export interface EmploymentGrowthData {
  level: 'metro' | 'neighborhood';
  title: string;
  geographyLabel?: string;
  source?: string;
  seasonallyAdjustedLabel?: string;
  asOfPeriod?: string;
  sourceYear?: number;
  currentEmployment?: number;
  currentJobs?: number;
  twelveMonth?: {
    jobsChange: number | null;
    pctChange?: number | null;
    label: string;
    periodLabel?: string;
  };
  tenYear?: {
    jobsChange: number | null;
    pctChange?: number | null;
    label: string;
    periodLabel?: string;
  };
  latestAnnual?: {
    jobsChange: number | null;
    pctChange?: number | null;
    label: string;
    periodLabel?: string;
  };
  jobsPer1000Residents?: number | null;
  chartSeries?: Array<{ label: string; employment: number; year?: number; period?: string }>;
}

const fmtJobs = (n: number | null | undefined) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Math.round(Number(n));
  const prefix = v > 0 ? '+' : '';
  return `${prefix}${v.toLocaleString()}`;
};

const fmtJobsPlain = (n: number | null | undefined) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return Math.round(Number(n)).toLocaleString();
};

const tone = (n: number | null | undefined) => {
  if (n == null || !Number.isFinite(Number(n)) || Number(n) === 0) return 'neutral';
  return Number(n) > 0 ? 'pos' : 'neg';
};

const StatCard: React.FC<{
  value: string;
  label: string;
  sub?: string;
  jobsChange?: number | null;
}> = ({ value, label, sub, jobsChange }) => (
  <div className={`emp-stat emp-stat--${tone(jobsChange)}`}>
    <div className="emp-stat__value">{value}</div>
    <div className="emp-stat__label">{label}</div>
    {sub && <div className="emp-stat__sub">{sub}</div>}
  </div>
);

const EmploymentGrowthSection: React.FC<{ data: EmploymentGrowthData }> = ({ data }) => {
  const chartData = useMemo(() => {
    const series = data.chartSeries || [];
    return series.map((row) => ({
      label: row.label,
      employment: row.employment,
    }));
  }, [data.chartSeries]);

  const primaryChange = data.level === 'metro' ? data.twelveMonth : data.latestAnnual;
  const secondaryChange = data.tenYear;
  const headlineJobs = data.currentEmployment ?? data.currentJobs;

  return (
    <section className="emp-section">
      <div className="emp-section__head">
        <div>
          <div className="emp-section__eyebrow">{data.title}</div>
          <h3 className="emp-section__title">{data.geographyLabel || 'Employment area'}</h3>
          <p className="emp-section__source">
            {data.source}
            {data.seasonallyAdjustedLabel ? ` · ${data.seasonallyAdjustedLabel}` : ''}
            {data.asOfPeriod ? ` · Through ${data.asOfPeriod}` : ''}
            {data.sourceYear ? ` · Source year ${data.sourceYear}` : ''}
          </p>
        </div>
        {headlineJobs != null && (
          <div className="emp-current">
            <span className="emp-current__label">Current jobs</span>
            <strong className="emp-current__value">{fmtJobsPlain(headlineJobs)}</strong>
          </div>
        )}
      </div>

      <div className="emp-stats">
        {primaryChange && (
          <StatCard
            value={fmtJobs(primaryChange.jobsChange)}
            label={primaryChange.label}
            sub={primaryChange.periodLabel}
            jobsChange={primaryChange.jobsChange}
          />
        )}
        {secondaryChange && (
          <StatCard
            value={fmtJobs(secondaryChange.jobsChange)}
            label={secondaryChange.label}
            sub={secondaryChange.periodLabel}
            jobsChange={secondaryChange.jobsChange}
          />
        )}
        {data.jobsPer1000Residents != null && (
          <StatCard
            value={String(data.jobsPer1000Residents)}
            label="Jobs per 1,000 residents"
            sub="Employment center vs. population growth"
          />
        )}
      </div>

      {chartData.length > 1 ? (
        <div className="emp-chart">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => `${Math.round(Number(v) / 1000)}k`}
                width={48}
              />
              <Tooltip formatter={(v: number) => [fmtJobsPlain(v), 'Total jobs']} />
              <Line
                type="monotone"
                dataKey="employment"
                stroke="#0f766e"
                strokeWidth={3}
                dot={{ r: 3, fill: '#0f766e' }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="emp-empty">Employment trend chart not available for this area.</p>
      )}
    </section>
  );
};

export default EmploymentGrowthSection;
